/**
 * Onboarding Panel General — trial, clientes vacíos y CTAs por módulo contratado.
 */

export const TRIAL_EXPIRED_USER_MESSAGE =
  "El periodo de prueba de esta instalación ya venció. Solicita activación de licencia para operar los módulos contratados.";

/** Texto corto en la barra de instalación (sin párrafo largo en columna de estado). */
export const TRIAL_EXPIRED_STATUS_HINT = "Prueba vencida — requiere activación.";

/** Cuerpo informativo para CTAs de activación en Panel General (trial vencido / licencia incómoda). */
export const TRIAL_LICENSE_ACTIVATION_BODY =
  "Tu prueba venció o tu licencia requiere atención. Puedes solicitar una licencia o activar una clave recibida desde Mi Negocio / Licencia.";

export const TRIAL_ACTIVE_STATUS_MESSAGE =
  "Tu periodo de prueba está activo. Puedes usar los módulos disponibles durante la prueba. Cuando estés listo, solicita tu licencia desde Mi Negocio / Licencia.";

export const TRIAL_ACTIVE_LICENSE_CTA_LABEL = "Mi Negocio / Solicitud de licencia";

export const LICENSE_ACTIVE_SECONDARY_CTA_LABEL = "Ver licencia / Mi Negocio";

export const LICENSE_RENEWAL_EXPANSION_CTA_LABEL = "Renovar o ampliar";

export const LICENSE_INACTIVE_SELECTOR_HINT =
  "Tu prueba o licencia requiere activación. Usa los accesos de licencia en el panel o en Mi Negocio / Licencia.";

export const TRIAL_EXPIRED_INSTALLATION_HINT =
  "Si acabas de registrarte y ves una fecha de prueba antigua, es posible que esta instalación ya hubiera usado el periodo de evaluación (documento de licencia compartido o correo con historial previo).";

export const EMPTY_CLIENTS_ONBOARDING_MESSAGE =
  "Aún no tienes clientes registrados. Puedes empezar registrando clientes operativos del despacho o clientes fiscales, según el módulo contratado.";

export const LICENSE_INACTIVE_ONBOARDING_MESSAGE =
  "Tu prueba está vencida o tu licencia no está activa. Solicita activación para operar módulos contratados.";

export function isTrialExpired(subscription = {}) {
  return subscription.mode === "trial" && !subscription.ok && subscription.reason === "trial_expired";
}

export function isTrialActive(subscription = {}) {
  return subscription.mode === "trial" && subscription.ok;
}

/** Días restantes hasta trial_ends_at (inclusive del día actual). */
export function trialDaysRemaining(subscription = {}, trialDaysConfigured = 3) {
  const raw = subscription?.trial_ends_at;
  if (!raw) return null;
  try {
    const end = new Date(String(raw));
    if (Number.isNaN(end.getTime())) return null;
    const now = new Date();
    const ms = end.setHours(23, 59, 59, 999) - now.getTime();
    return Math.max(0, Math.ceil(ms / 86400000));
  } catch {
    return null;
  }
}

/**
 * CTAs del selector vacío según entitlements (sin llamar APIs).
 */
export function buildEmptyClientsOnboardingActions({
  hasDespacho = false,
  hasVisor = false,
  hasTramites = false,
} = {}) {
  const actions = [];
  if (hasDespacho || hasVisor || hasTramites) {
    actions.push({
      id: "fiscal-clientes",
      label: "Gestionar clientes fiscales",
      to: "/datos-maestros",
      testId: "panel-hub-cta-fiscal-clientes",
      priority: 1,
    });
  }
  if (hasDespacho) {
    actions.push({
      id: "despacho-operativos",
      label: "Asignacion de Empresas a Auxiliares",
      to: "/despacho/clientes",
      testId: "panel-hub-cta-despacho-clientes",
      priority: 2,
    });
  }
  return actions.sort((a, b) => a.priority - b.priority);
}

export function shouldShowLicenseInactiveBanner(subscription = {}, license = {}) {
  if (isTrialExpired(subscription)) return true;
  if (subscription.mode === "licensed" && license.valid === false) return true;
  if (subscription.mode === "licensed" && !subscription.ok) return true;
  return false;
}

export function resolveInstallationBarTrialState(subscription = {}, trialDaysConfigured = 3) {
  const expired = isTrialExpired(subscription);
  const active = isTrialActive(subscription);
  const daysLeft = trialDaysRemaining(subscription, trialDaysConfigured);
  return {
    expired,
    active,
    daysLeft,
    estadoLabel: expired ? "Prueba vencida" : active ? "Prueba activa" : null,
    showExpiredHint: expired,
  };
}
